import { useState, useEffect } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { Buffer } from "buffer";

interface BlobItem {
  id: string;
  documentName: string;
  documentType: string;
  owner: string;
  status: string;
  lastModified: string;
  version: string;
  blobPath: string;
  size?: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

const useDocumentManagement = (containerName: string = "copilotdms") => {
  const [loading, setLoading] = useState<boolean>(true);
  const [documents, setDocuments] = useState<BlobItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(
    undefined
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<BlobItem | null>(
    null
  );
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isInitialize, setIsInitialize] = useState<boolean>(true);
  const [latestIndex, setLatestIndex] = useState<number>(0);

  const getFileType = (filename: string): string => {
    const extension = filename.split(".").pop();
    return extension ? extension.toUpperCase() : "";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleViewDocument = async (document: BlobItem) => {
    try {
      setSelectedDocument(document);
      const baseUrl = `https://copilotdms.blob.core.windows.net/copilotdms/`;
      const encodedPath = encodeURIComponent(document.blobPath);

      const fullUrl = `${baseUrl}${encodedPath}${process.env.REACT_APP_AZURE_SAS_TOKEN}`;

      setDocumentUrl(fullUrl);
      setViewerOpen(true);
    } catch (err) {
      console.error("Error viewing document:", err);
      setSnackbar({
        open: true,
        message: "Error viewing document. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDelete = async (document: BlobItem) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${document.documentName}?`
      )
    ) {
      try {
        const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
        if (!sasToken) {
          throw new Error("Azure Storage SAS token is not configured");
        }

        const blobServiceClient = new BlobServiceClient(
          `https://copilotdms.blob.core.windows.net${sasToken}`
        );
        const containerClient =
          blobServiceClient.getContainerClient(containerName);
        const blobClient = containerClient.getBlobClient(document.blobPath);

        await blobClient.delete();

        setDocuments((prev) => prev.filter((doc) => doc.id !== document.id));
        setSnackbar({
          open: true,
          message: "Document deleted successfully",
          severity: "success",
        });
      } catch (err) {
        console.error("Error deleting document:", err);
        setSnackbar({
          open: true,
          message: "Error deleting document. Please try again.",
          severity: "error",
        });
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
      if (!sasToken) {
        throw new Error("Azure Storage SAS token is not configured");
      }

      const blobServiceClient = new BlobServiceClient(
        `https://copilotdms.blob.core.windows.net${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlobClient(selectedFile.name);
      const blockBlobClient = blobClient.getBlockBlobClient();

      const arrayBuffer = await selectedFile.arrayBuffer();
      const blockId = Buffer.from(Date.now().toString()).toString("base64");

      await blockBlobClient.stageBlock(
        blockId,
        arrayBuffer,
        arrayBuffer.byteLength
      );
      await blockBlobClient.commitBlockList([blockId], {
        blobHTTPHeaders: {
          blobContentType: selectedFile.type,
        },
      });

      setUploadProgress(100);
      fetchDocuments();

      setUploadOpen(false);
      setSelectedFile(null);
      setUploadProgress(0);
      setSnackbar({
        open: true,
        message: "Document uploaded successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error uploading document:", err);
      setSnackbar({
        open: true,
        message: "Error uploading document. Please try again.",
        severity: "error",
      });
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
      if (!sasToken) {
        throw new Error("Azure Storage SAS token is not configured");
      }

      const blobServiceClient = new BlobServiceClient(
        `https://copilotdms.blob.core.windows.net${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);

      // Pagination settings
      const iterator = containerClient.listBlobsFlat().byPage({
        continuationToken: nextPageToken,
        maxPageSize: paginationModel.pageSize,
      });

      const response = await iterator.next();
      if (response.value) {
        const blobs: BlobItem[] = [];
        let index = 0;
        for (const blob of response.value.segment.blobItems) {
          const properties = await containerClient
            .getBlobClient(blob.name)
            .getProperties();

          blobs.push({
            id: (latestIndex + index).toString(),
            documentName: decodeURIComponent(blob.name),
            documentType: getFileType(blob.name),
            owner: properties.metadata?.owner || "DMS",
            status: "Active",
            lastModified: new Date(
              blob.properties.lastModified!
            ).toLocaleString(),
            version: properties.versionId || "v1.0",
            blobPath: blob.name,
            size: formatFileSize(blob.properties.contentLength || 0),
          });
          index++;
          console.log(latestIndex + index);
        }

        setLatestIndex(latestIndex + index);

        setDocuments((prev) => [...prev, ...blobs]);
        setNextPageToken(response.value.continuationToken);
        setTotalRowCount(
          (prev) => prev + response.value.segment.blobItems.length
        );
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while fetching documents"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationModelChange = (newPaginationModel: {
    page: number;
    pageSize: number;
  }) => {
    if (isInitialize) {
      setDocuments(documents.slice(10));
      setIsInitialize(false);
    }
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    if ((paginationModel.page + 1) * paginationModel.pageSize < latestIndex) {
      return;
    }
    fetchDocuments();
  }, [paginationModel]);

  return {
    loading,
    documents,
    error,
    uploadOpen,
    selectedFile,
    uploadProgress,
    viewerOpen,
    selectedDocument,
    documentUrl,
    snackbar,
    totalRowCount,
    paginationModel,
    setUploadOpen,
    setSelectedFile,
    setViewerOpen,
    setSnackbar,
    handleViewDocument,
    handleDelete,
    handleFileUpload,
    handlePaginationModelChange,
  };
};

export default useDocumentManagement;
